import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Lock, Settings, Users, MessageSquare, Save, 
  Plus, Trash2, Edit, ArrowLeft, Eye, EyeOff, ChevronUp, ChevronDown,
  GripVertical, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import ThemeWrapper from '@/components/ThemeWrapper';
import { choiceLabel, choicesForAdminForm, emptyChoiceForForm } from '@/lib/questionChoices';
import { parseBackendUtcToDate } from '@/lib/backendDate';
import { downloadSessionZip } from '@/lib/sessionExportZip';
import { getSession } from '@/services/dataRepo';
import { adminLogout, getAuthMode } from '@/services/nocodebackend';

/** Un seul emplacement toast pour les suppressions de session (évite l’empilement). */
const SESSION_DELETE_TOAST_ID = 'admin-session-delete';
const SESSION_DOWNLOAD_TOAST_ID = 'admin-session-download';

/** Textes robot du vocal prénom (réglages globaux), édités avec la question Identité. */
const IDENTITY_ROBOT_TEXT_KEYS = ['ask_name_audio', 'ask_name_audio_skip', 'identity_voice_done'];

const IDENTITY_VOICE_FIELD_LABELS = {
  ask_name_audio: 'Texte robot — avant le micro (prénom vocal)',
  ask_name_audio_skip: 'Texte robot — si l’enfant passe l’enregistrement vocal',
  identity_voice_done: 'Texte robot — après l’enregistrement du prénom vocal',
};

const emptyIdentityRobotDraft = () =>
  Object.fromEntries(IDENTITY_ROBOT_TEXT_KEYS.map((k) => [k, '']));

/** Libellés QCM pour une réponse : snapshot enregistré ou question actuelle du parcours. */
function parseResponseChoicesOffered(raw) {
  if (Array.isArray(raw) && raw.length > 0) return raw.map((x) => String(x).trim()).filter(Boolean);
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p) && p.length > 0) return p.map((x) => String(x).trim()).filter(Boolean);
    } catch {
      /* ignore */
    }
  }
  return null;
}

function responseMcChoiceLabels(resp, questionsList) {
  let labels = parseResponseChoicesOffered(resp?.choices_offered);
  if (labels?.length) return labels;
  const qid = resp?.question_id != null ? String(resp.question_id) : '';
  const q = Array.isArray(questionsList)
    ? questionsList.find((x) => x && String(x.id) === qid)
    : null;
  if (q?.type === 'multiple_choice' && Array.isArray(q.choices)) {
    const ls = q.choices.map((c) => choiceLabel(c).trim()).filter(Boolean);
    return ls.length ? ls : null;
  }
  return null;
}

const AdminPanel = () => {
  const navigate = useNavigate();
  const { 
    isAuthenticated, 
    setIsAuthenticated,
    verifyPassword, 
    fetchSettings, 
    updateSettings,
    fetchSessions,
    deleteSession,
    fetchQuestions,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    reorderQuestions,
    questions,
    settings
  } = useApp();

  const [password, setPassword] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [authMode, setAuthMode] = useState('loading');
  const [authErrorDetail, setAuthErrorDetail] = useState('');
  const [authEnvVisibility, setAuthEnvVisibility] = useState(null);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [refreshingSessionId, setRefreshingSessionId] = useState(null);
  const [downloadingSessionId, setDownloadingSessionId] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [identityRobotDraft, setIdentityRobotDraft] = useState(emptyIdentityRobotDraft);
  const [isEditQuestionDialogOpen, setIsEditQuestionDialogOpen] = useState(false);
  const [isAddQuestionDialogOpen, setIsAddQuestionDialogOpen] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    text: '',
    robot_text: '',
    type: 'multiple_choice',
    choices: [
      emptyChoiceForForm(),
      emptyChoiceForForm(),
      emptyChoiceForForm(),
      emptyChoiceForForm(),
    ],
    order: 1,
    thank_text: ''
  });
  const [settingsForm, setSettingsForm] = useState({
    default_theme: 'default',
    robot_texts: {},
    ui_texts: {}
  });
  const settingsFormRef = useRef(settingsForm);
  const forcedIdentityOrderRef = useRef('');
  const sessionsFetchSeqRef = useRef(0);

  const loadAuthMode = useCallback(async () => {
    setAuthMode('loading');
    setAuthErrorDetail('');
    setAuthEnvVisibility(null);
    const m = await getAuthMode();
    setAuthMode(m.mode || 'error');
    setAuthErrorDetail(m.message || '');
    setAuthEnvVisibility(m.envVisibility || null);
  }, []);

  useEffect(() => {
    void loadAuthMode();
  }, [loadAuthMode]);

  const loadSessions = useCallback(async (opts = {}) => {
    const silent = Boolean(opts.silent);
    const seq = ++sessionsFetchSeqRef.current;
    if (!silent) {
      setIsLoadingSessions(true);
    }
    try {
      const data = await fetchSessions();
      if (seq !== sessionsFetchSeqRef.current) return;
      setSessions(Array.isArray(data) ? data : []);
    } finally {
      if (seq === sessionsFetchSeqRef.current) {
        setIsLoadingSessions(false);
      }
    }
  }, [fetchSessions]);

  const loadData = useCallback(async () => {
    await Promise.all([
      fetchSettings(),
      fetchQuestions(),
      loadSessions()
    ]);
  }, [fetchSettings, fetchQuestions, loadSessions]);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, loadData]);

  // Safety: if "Identité" was previously moved away (order already persisted),
  // force it back to the top as soon as questions are loaded.
  useEffect(() => {
    if (!questions?.length) return;
    const identityIndex = questions.findIndex((q) => q.type === 'identity');
    if (identityIndex <= 0) return;

    const identity = questions[identityIndex];
    const target = [
      identity.id,
      ...questions.filter((q) => q.id !== identity.id).map((q) => q.id),
    ];
    const signature = target.join('|');
    if (forcedIdentityOrderRef.current === signature) return;
    forcedIdentityOrderRef.current = signature;

    // Don't block UI; reorder backend + context will update the list.
    void reorderQuestions(target);
  }, [questions, reorderQuestions]);

  useEffect(() => {
    if (settings) {
      setSettingsForm({
        robot_texts: settings.robot_texts || {},
        ui_texts: settings.ui_texts || {}
      });
    }
  }, [settings]);

  useEffect(() => {
    settingsFormRef.current = settingsForm;
  }, [settingsForm]);

  const refreshSessionOnOpen = async (isOpen, sessionId) => {
    if (!isOpen || !sessionId) return;
    setRefreshingSessionId(sessionId);
    try {
      await loadSessions({ silent: true });
    } finally {
      setRefreshingSessionId(null);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (authMode === 'loading') return;
    if (authMode === 'error') {
      toast.error("Impossible de se connecter : le mode d'authentification n'a pas pu être déterminé.");
      return;
    }
    if (authMode === 'unconfigured') {
      toast.error('Configuration serveur incomplète (voir variables Netlify).');
      return;
    }
    if (authMode === 'ncb' && !adminEmail.trim()) {
      toast.error('Indiquez votre adresse e-mail.');
      return;
    }
    const result = await verifyPassword({
      password,
      email: adminEmail.trim(),
    });
    if (result?.ok) {
      toast.success('Connexion réussie');
    } else {
      toast.error(result?.detail || 'E-mail ou mot de passe incorrect');
    }
  };

  const handleLogout = async () => {
    sessionsFetchSeqRef.current += 1;
    try {
      await adminLogout();
    } catch {
      /* ignore */
    }
    setIsAuthenticated(false);
    setPassword('');
    setAdminEmail('');
    void loadAuthMode();
    setSessions([]);
    setIsLoadingSessions(true);
  };

  const handleSaveSettings = async () => {
    // Force blur so onBlur-driven fields are flushed before save.
    if (typeof document !== 'undefined') {
      const active = document.activeElement;
      if (active && typeof active.blur === 'function') {
        active.blur();
      }
    }
    await new Promise((resolve) => setTimeout(resolve, 0));

    const current = settingsFormRef.current;
    const updateData = {
      robot_texts: current.robot_texts,
      ui_texts: current.ui_texts
    };

    const toastId = toast.loading('Sauvegarde en cours...');
    const updated = await updateSettings(updateData);
    if (!updated) {
      toast.error('Échec de la sauvegarde', { id: toastId });
      return;
    }
    toast.success('Paramètres sauvegardés', { id: toastId });
  };

  const handleAddQuestion = async () => {
    const questionData = {
      ...newQuestion,
      choices:
        newQuestion.type === 'multiple_choice'
          ? newQuestion.choices
              .filter((c) => choiceLabel(c).trim() !== '')
              .map((c) => ({
                label: choiceLabel(c).trim(),
                voice_followup: Boolean(c.voice_followup),
                followup_robot_text:
                  c.voice_followup && typeof c.followup_robot_text === 'string'
                    ? c.followup_robot_text.trim()
                    : '',
              }))
          : [],
      order: questions.length + 1,
      encouragement_text: null,
      thank_text: (newQuestion.thank_text || '').trim() || null
    };
    
    const toastId = toast.loading('Ajout en cours...');
    const created = await createQuestion(questionData);
    if (!created) {
      toast.error("Échec de l'ajout", { id: toastId });
      return;
    }
    toast.success('Question ajoutée', { id: toastId });
    setIsAddQuestionDialogOpen(false);
    setNewQuestion({
      text: '',
      robot_text: '',
      type: 'multiple_choice',
      choices: [
        emptyChoiceForForm(),
        emptyChoiceForForm(),
        emptyChoiceForForm(),
        emptyChoiceForForm(),
      ],
      order: 1,
      thank_text: ''
    });
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuestion) return;
    const toastId = toast.loading('Modification en cours...');
    const updated = await updateQuestion(editingQuestion.id, {
      ...editingQuestion,
      choices:
        editingQuestion.type === 'multiple_choice'
          ? (editingQuestion.choices || [])
              .filter((c) => choiceLabel(c).trim() !== '')
              .map((c) => ({
                label: choiceLabel(c).trim(),
                voice_followup: Boolean(c.voice_followup),
                followup_robot_text:
                  c.voice_followup && typeof c.followup_robot_text === 'string'
                    ? c.followup_robot_text.trim()
                    : '',
              }))
          : [],
      encouragement_text: null,
      thank_text: (editingQuestion.thank_text || '').trim() || null
    });
    if (!updated) {
      toast.error('Échec de la modification', { id: toastId });
      return;
    }

    if (editingQuestion.type === 'identity') {
      const patch = {};
      for (const k of IDENTITY_ROBOT_TEXT_KEYS) {
        patch[k] = identityRobotDraft[k] ?? '';
      }
      const nextSettings = await updateSettings({ robot_texts: patch });
      if (!nextSettings) {
        toast.error('Question enregistrée, mais échec des textes prénom (réglages).', { id: toastId });
        setEditingQuestion(null);
        setIsEditQuestionDialogOpen(false);
        return;
      }
    }

    toast.success('Question modifiée', { id: toastId });
    setEditingQuestion(null);
    setIsEditQuestionDialogOpen(false);
  };

  const handleDeleteQuestion = async (id) => {
    const toastId = toast.loading('Suppression en cours...');
    const ok = await deleteQuestion(id);
    if (!ok) {
      toast.error("Échec de la suppression", { id: toastId });
      return;
    }
    toast.success('Question supprimée', { id: toastId });
  };

  const handleMoveQuestion = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= questions.length) return;
    const lockedIdentity = questions.find((q) => q.type === 'identity');
    const movedQuestion = questions[index];

    // Identity must stay first in timeline.
    if (movedQuestion?.type === 'identity') {
      toast.error("La question Identité est verrouillée en première position");
      return;
    }

    const newOrder = [...questions];
    const [moved] = newOrder.splice(index, 1);
    newOrder.splice(newIndex, 0, moved);

    // Safety net: if identity exists, force it at index 0.
    if (lockedIdentity) {
      const withoutIdentity = newOrder.filter((q) => q.id !== lockedIdentity.id);
      newOrder.length = 0;
      newOrder.push(lockedIdentity, ...withoutIdentity);
    }

    const ids = newOrder.map(q => q.id);
    const toastId = toast.loading('Mise à jour de l’ordre...');
    const ok = await reorderQuestions(ids);
    if (!ok) {
      toast.error("Échec de la mise à jour de l’ordre", { id: toastId });
      return;
    }
    toast.success('Ordre modifié', { id: toastId });
  };

  const handleDeleteSession = async (id) => {
    const previous = sessions;
    setSessions((prev) => prev.filter((s) => s.id !== id));
    toast.loading('Suppression en cours…', { id: SESSION_DELETE_TOAST_ID });
    const ok = await deleteSession(id);
    if (ok) {
      toast.success('Session supprimée', { id: SESSION_DELETE_TOAST_ID, duration: 2200 });
      return;
    }
    setSessions(previous);
    toast.error('Échec de la suppression', { id: SESSION_DELETE_TOAST_ID, duration: 4000 });
  };

  const handleDownloadSession = async (session) => {
    setDownloadingSessionId(session.id);
    toast.loading('Préparation du fichier ZIP…', { id: SESSION_DOWNLOAD_TOAST_ID });
    try {
      const fresh = await getSession(session.id);
      if (!fresh) {
        toast.error('Session introuvable', { id: SESSION_DOWNLOAD_TOAST_ID, duration: 4000 });
        return;
      }
      await downloadSessionZip(fresh, questions);
      toast.success('Téléchargement lancé', { id: SESSION_DOWNLOAD_TOAST_ID, duration: 2200 });
    } catch (e) {
      console.error(e);
      toast.error('Échec de la préparation du ZIP', { id: SESSION_DOWNLOAD_TOAST_ID, duration: 4000 });
    } finally {
      setDownloadingSessionId(null);
    }
  };

  const formatDate = (dateString) => {
    const d = parseBackendUtcToDate(dateString);
    if (!d) return '—';
    return d.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const robotTextLabels = {
    welcome: "Message d'accueil",
    ask_name_audio: "Demande du prénom (vocal)",
    ask_name_audio_skip: "Si l'enfant passe l'enregistrement",
    identity_voice_done: "Après prénom vocal (avant la suite)",
    question_intro: "Introduction aux questions",
    read_choices: "Avant de lire les choix",
    thank_response: "Remerciement (par défaut)",
    goodbye: "Message de fin"
  };

  const uiTextLabels = {
    welcome_title: "Titre page d'accueil",
    welcome_subtitle: "Sous-titre page d'accueil",
    intro_title: "Titre page introduction",
    intro_message: "Message écrit d'introduction",
    intro_button: "Texte bouton introduction",
    start_button: "Bouton démarrer",
    name_title: "Titre page prénom",
    name_placeholder: "Placeholder champ prénom",
    name_button: "Bouton valider prénom",
    voice_title: "Titre enregistrement vocal",
    voice_instruction: "Instruction micro",
    voice_listening: "Texte écoute en cours",
    voice_recorded: "Texte après enregistrement",
    continue_button: "Bouton continuer",
    skip_link: "Lien passer l'étape",
    send_button: "Texte bouton confirmation (enfant)",
    skip_question: "Lien passer la question",
    choice_voice_stop_sends: "QCM voix : consigne bouton rouge",
    choice_voice_record_then_stop: "QCM voix : consigne micro puis rouge",
    mc_voice_skipped_note: "Compte rendu si l'enfant passe le micro après un choix QCM",
    thank_title: "Titre page remerciement",
    thank_message: "Message remerciement",
    thank_proud: "Message fierté",
    home_button: "Bouton retour accueil"
  };

  const questionTypeLabels = {
    identity: 'Identité',
    multiple_choice: 'Choix multiple',
    open_voice: 'Question ouverte',
  };

  const autoResizeTextarea = (event) => {
    const el = event.target;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <ThemeWrapper className="flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-slate-950/55 pointer-events-none" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10">
          <Card className="w-full max-w-md bg-slate-900/92 border-slate-600 shadow-2xl shadow-slate-950/40">
            <CardHeader className="text-center pt-6 pb-2">
              <Lock className="w-12 h-12 mx-auto text-cyan-400" aria-hidden />
            </CardHeader>
            <CardContent>
              {authMode === 'loading' && (
                <p className="text-center text-slate-400 py-8">Chargement…</p>
              )}
              {authMode === 'error' && (
                <div className="space-y-4 text-center text-slate-300 text-sm px-2">
                  <p>{authErrorDetail || 'Erreur inconnue.'}</p>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void loadAuthMode()}
                    className="w-full bg-slate-700 text-white hover:bg-slate-600"
                  >
                    Réessayer
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => navigate('/')} className="w-full text-slate-300">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour
                  </Button>
                </div>
              )}
              {authMode === 'unconfigured' && (
                <div className="space-y-4 text-center text-slate-300 text-sm px-2">
                  <p className="font-medium text-white">
                    Connexion enseignant : e-mail + mot de passe NoCodeBackend
                  </p>
                  <p className="text-slate-400">
                    Ce formulaire n’apparaît qu’une fois la User Auth configurée sur le serveur. Voici ce que le site
                    détecte :
                  </p>
                  {authErrorDetail ? (
                    <p className="text-left text-sm text-amber-100/95 leading-relaxed border border-amber-500/40 rounded-lg p-3 bg-amber-950/35 whitespace-pre-wrap">
                      {authErrorDetail}
                    </p>
                  ) : null}
                  {authEnvVisibility ? (
                    <div className="text-left rounded-lg border border-slate-600 bg-slate-950/60 p-3 text-xs text-slate-300 space-y-2">
                      <p className="font-semibold text-slate-200">Ce que le serveur voit (sans afficher de secret)</p>
                      <p className="text-slate-500">
                        Si <span className="text-slate-300">NCB_SECRET_KEY</span> est « non » alors que vous l’avez créée sur Netlify, le problème vient du <strong>nom</strong>, du <strong>contexte</strong> (p.ex. variable limitée aux previews), ou d’un <strong>déploiement</strong> pas relancé après l’enregistrement.
                      </p>
                      <ul className="font-mono space-y-0.5 text-[11px]">
                        {Object.entries(authEnvVisibility.secrets || {}).map(([k, v]) => (
                          <li key={k}>
                            {k}: <span className={v ? 'text-emerald-400' : 'text-red-400'}>{v ? 'oui' : 'non'}</span>
                          </li>
                        ))}
                        <li>
                          NCB_INSTANCE:{' '}
                          <span className={authEnvVisibility.NCB_INSTANCE ? 'text-emerald-400' : 'text-red-400'}>
                            {authEnvVisibility.NCB_INSTANCE ? 'oui' : 'non'}
                          </span>
                        </li>
                        <li>
                          NOCODEBACKEND_INSTANCE:{' '}
                          <span className={authEnvVisibility.NOCODEBACKEND_INSTANCE ? 'text-emerald-400' : 'text-red-400'}>
                            {authEnvVisibility.NOCODEBACKEND_INSTANCE ? 'oui' : 'non'}
                          </span>
                        </li>
                        <li>
                          NOCODEBACKEND_TOKEN (API tables):{' '}
                          <span className={authEnvVisibility.NOCODEBACKEND_TOKEN ? 'text-emerald-400' : 'text-red-400'}>
                            {authEnvVisibility.NOCODEBACKEND_TOKEN ? 'oui' : 'non'}
                          </span>
                        </li>
                        <li>
                          Fonction Netlify (lambda):{' '}
                          <span className={authEnvVisibility.netlifyFunction ? 'text-emerald-400' : 'text-amber-400'}>
                            {authEnvVisibility.netlifyFunction ? 'oui' : 'non (hors prod / local ?)'}
                          </span>
                        </li>
                      </ul>
                    </div>
                  ) : null}
                  <Button type="button" variant="ghost" onClick={() => navigate('/')} className="text-slate-300">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour
                  </Button>
                </div>
              )}
              {authMode === 'ncb' && (
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                    <label className="text-xs text-slate-400 block mb-1">E-mail</label>
                    <Input
                      type="email"
                      autoComplete="username"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      placeholder="Email"
                      className="text-lg py-5 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                    />
                  </div>
                <div className="relative">
                  <Input
                    data-testid="admin-password-input"
                    type={showLoginPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mot de passe"
                    className="text-lg py-6 pr-14 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 px-4 text-slate-300 hover:text-white transition-colors"
                    aria-label={showLoginPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    title={showLoginPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  >
                    {showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <Button
                  data-testid="admin-login-btn"
                  type="submit"
                  disabled={authMode === 'loading' || authMode === 'error' || authMode === 'unconfigured'}
                  className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold py-6"
                >
                  Connexion
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/')}
                  className="w-full text-slate-300 hover:text-white hover:bg-slate-700"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
              </form>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </ThemeWrapper>
    );
  }

  // Admin Dashboard
  return (
    <ThemeWrapper>
      <div className="absolute inset-0 bg-slate-950/55 pointer-events-none" />
      {/* Header */}
      <header className="bg-slate-900/95 border-b border-slate-600 sticky top-0 z-50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Settings className="w-8 h-8 text-cyan-400" />
            <h1 className="text-xl font-bold text-white">Panneau Professeur</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-slate-600 text-slate-200 hover:bg-emerald-500 hover:text-white shadow-none hover:shadow-none"
            >
              <Eye className="w-4 h-4 mr-2" />
              Aperçu
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-slate-300 hover:text-white hover:bg-red-500 shadow-none hover:shadow-none"
            >
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="sessions" className="space-y-6">
          <TabsList className="flex h-12 w-full max-w-sm items-stretch gap-1 rounded-full border border-white/25 bg-slate-800 p-1 text-slate-400 shadow-sm shadow-black/30">
            <TabsTrigger
              value="sessions"
              className="flex h-full min-h-0 flex-1 items-center justify-center gap-2 rounded-full px-3 text-sm font-semibold text-slate-400 transition-colors data-[state=active]:bg-cyan-500 data-[state=active]:text-slate-900 data-[state=active]:shadow-none"
            >
              <Users className="size-4 shrink-0" />
              Sessions
            </TabsTrigger>
            <TabsTrigger
              value="questions"
              className="flex h-full min-h-0 flex-1 items-center justify-center gap-2 rounded-full px-3 text-sm font-semibold text-slate-400 transition-colors data-[state=active]:bg-cyan-500 data-[state=active]:text-slate-900 data-[state=active]:shadow-none"
            >
              <MessageSquare className="size-4 shrink-0" />
              Parcours
            </TabsTrigger>
          </TabsList>

          {/* Sessions Tab */}
          <TabsContent value="sessions">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Sessions des enfants</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingSessions ? (
                  <p className="text-center text-slate-400 py-8">Chargement des sessions...</p>
                ) : sessions.length === 0 ? (
                  <p className="text-center text-slate-400 py-8">Aucune session enregistrée</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-300">Prénom</TableHead>
                          <TableHead className="text-slate-300">Date</TableHead>
                          <TableHead className="text-slate-300">Statut</TableHead>
                          <TableHead className="text-slate-300">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sessions.map((session) => (
                          <TableRow key={session.id} className="border-slate-700">
                            <TableCell className="font-medium text-white">{session.child_name}</TableCell>
                            <TableCell className="text-slate-300">{formatDate(session.created_at)}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs ${
                                session.completed 
                                  ? 'bg-emerald-500/20 text-emerald-400' 
                                  : 'bg-amber-500/20 text-amber-400'
                              }`}>
                                {session.completed ? 'Terminé' : 'En cours'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Dialog onOpenChange={(open) => refreshSessionOnOpen(open, session.id)}>
                                  <DialogTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="border-slate-600 text-slate-200 hover:bg-slate-700 shadow-none hover:shadow-none"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-slate-800 border-slate-700 text-white">
                                    <DialogHeader>
                                      <DialogTitle className="text-white">Session de {session.child_name}</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                      {session.child_name_audio_url && (
                                        <div className="p-4 bg-slate-700 rounded-lg">
                                          <p className="font-medium mb-2 text-cyan-400">Prénom enregistré :</p>
                                          <audio src={session.child_name_audio_url} controls className="w-full" />
                                        </div>
                                      )}
                                      
                                      <div>
                                        <p className="font-medium mb-3 text-cyan-400">Réponses ({session.responses?.length || 0}) :</p>
                                        {refreshingSessionId === session.id && (
                                          <p className="text-xs text-slate-400 mb-2">Actualisation des réponses...</p>
                                        )}
                                        {session.responses?.length > 0 ? (
                                          <div className="space-y-4">
                                            {session.responses.map((resp, idx) => (
                                              <div key={idx} className="p-4 bg-slate-700 rounded-lg border-l-4 border-cyan-500">
                                                <p className="font-bold text-white mb-2">
                                                  Q{idx + 1}: {resp.question_text}
                                                </p>
                                                
                                                {(() => {
                                                  const selected = resp.choice_selected != null
                                                    ? String(resp.choice_selected).trim()
                                                    : '';
                                                  const labels = responseMcChoiceLabels(resp, questions);
                                                  if (labels?.length) {
                                                    return (
                                                      <div className="mb-2 space-y-1">
                                                        <p className="text-sm text-slate-400">Propositions :</p>
                                                        <ul className="m-0 list-none space-y-1 p-0">
                                                          {labels.map((label, i) => {
                                                            const t = String(label).trim();
                                                            const isSel = selected && t === selected;
                                                            return (
                                                              <li
                                                                key={`${i}-${t.slice(0, 24)}`}
                                                                className={
                                                                  isSel
                                                                    ? 'font-semibold text-emerald-400'
                                                                    : 'text-red-400'
                                                                }
                                                              >
                                                                {label}
                                                                {isSel ? (
                                                                  <span className="ml-2 text-xs font-normal text-emerald-300/90">
                                                                    (choix de l’enfant)
                                                                  </span>
                                                                ) : null}
                                                              </li>
                                                            );
                                                          })}
                                                        </ul>
                                                        {selected && !labels.some((l) => String(l).trim() === selected) ? (
                                                          <p className="mt-1 text-sm text-amber-300">
                                                            Choix enregistré (hors liste) : <span className="font-semibold">{selected}</span>
                                                          </p>
                                                        ) : null}
                                                      </div>
                                                    );
                                                  }
                                                  if (selected) {
                                                    return (
                                                      <p className="mb-2 text-emerald-400">
                                                        Choix : <span className="font-bold">{selected}</span>
                                                      </p>
                                                    );
                                                  }
                                                  return null;
                                                })()}
                                                
                                                {resp.text_response && (
                                                  <p className="text-slate-300 mb-2">
                                                    Texte : "{resp.text_response}"
                                                  </p>
                                                )}
                                                
                                                {resp.audio_url && (
                                                  <div className="mt-2">
                                                    <p className="text-sm text-slate-400 mb-1">Audio :</p>
                                                    <audio src={resp.audio_url} controls className="w-full" />
                                                  </div>
                                                )}
                                                
                                                {!resp.choice_selected &&
                                                  !resp.text_response &&
                                                  !resp.audio_url &&
                                                  !responseMcChoiceLabels(resp, questions)?.length && (
                                                  <p className="text-slate-500 italic">Pas de réponse</p>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <p className="text-slate-500">Aucune réponse</p>
                                        )}
                                      </div>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={downloadingSessionId === session.id}
                                  title="Télécharger la session (ZIP)"
                                  className="border-slate-600 text-slate-200 hover:bg-slate-700 shadow-none hover:shadow-none"
                                  onClick={() => handleDownloadSession(session)}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteSession(session.id)}
                                  className="bg-red-500/20 text-red-400 hover:bg-red-500/30 shadow-none hover:shadow-none"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Parcours Tab (questions + paramètres dans un seul flux) */}
          <TabsContent value="questions">
            <div className="flex flex-col gap-6">
              {/* Début du parcours */}
              <Accordion type="single" collapsible defaultValue="start-timeline" className="rounded-lg border border-slate-700 bg-slate-800 px-3 overflow-visible">
                <AccordionItem value="start-timeline" className="border-slate-700">
                  <AccordionTrigger className="admin-accordion-trigger text-white hover:no-underline">Début du parcours (timeline)</AccordionTrigger>
                  <AccordionContent className="overflow-visible">
                <CardContent className="space-y-4 px-0 overflow-visible">
                  <div className="p-3 bg-slate-700/40 rounded-lg space-y-3">
                    <p className="text-sm font-semibold text-cyan-300">Accueil</p>
                    <div className="grid grid-cols-1 gap-2 items-start">
                      <div className="grid md:grid-cols-[180px_minmax(0,1fr)] gap-2 items-center">
                        <label className="text-sm text-purple-400 font-medium">Titre</label>
                        <Input
                          defaultValue={settingsForm.ui_texts.welcome_title || ''}
                          onBlur={(e) => setSettingsForm(prev => ({
                            ...prev,
                            ui_texts: { ...prev.ui_texts, welcome_title: e.target.value }
                          }))}
                          className="h-9 bg-slate-600 border-slate-500 text-white"
                        />
                      </div>
                      <div className="grid md:grid-cols-[180px_minmax(0,1fr)] gap-2 items-start">
                        <label className="text-sm text-purple-400 font-medium md:pt-2">Message écrit</label>
                        <Textarea
                          defaultValue={settingsForm.ui_texts.welcome_subtitle || ''}
                          onBlur={(e) => setSettingsForm(prev => ({
                            ...prev,
                            ui_texts: { ...prev.ui_texts, welcome_subtitle: e.target.value }
                          }))}
                          rows={1}
                          onInput={autoResizeTextarea}
                          onFocus={autoResizeTextarea}
                          className="min-h-9 resize-none overflow-hidden bg-slate-600 border-slate-500 text-white"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-[180px_minmax(0,1fr)] gap-2 items-start">
                      <label className="text-sm text-cyan-400 font-medium md:pt-2">Message vocal</label>
                      <Textarea
                        defaultValue={settingsForm.robot_texts.welcome || ''}
                        onBlur={(e) => setSettingsForm(prev => ({
                          ...prev,
                          robot_texts: { ...prev.robot_texts, welcome: e.target.value }
                        }))}
                        rows={1}
                        onInput={autoResizeTextarea}
                        onFocus={autoResizeTextarea}
                        className="min-h-9 resize-none overflow-hidden bg-slate-600 border-slate-500 text-white"
                      />
                    </div>
                    <div className="grid md:grid-cols-[180px_minmax(0,1fr)] gap-2 items-center">
                      <label className="text-sm text-purple-400 font-medium">Texte du bouton démarrer</label>
                      <Input
                        defaultValue={settingsForm.ui_texts.start_button || ''}
                        onBlur={(e) => setSettingsForm(prev => ({
                          ...prev,
                          ui_texts: { ...prev.ui_texts, start_button: e.target.value }
                        }))}
                        className="h-9 bg-slate-600 border-slate-500 text-white"
                      />
                    </div>
                  </div>

                  <div className="p-3 bg-slate-700/40 rounded-lg space-y-3">
                    <p className="text-sm font-semibold text-cyan-300">Introduction</p>
                    <div className="grid grid-cols-1 gap-2 items-start">
                      <div className="grid md:grid-cols-[180px_minmax(0,1fr)] gap-2 items-center">
                        <label className="text-sm text-purple-400 font-medium">Titre</label>
                        <Input
                          defaultValue={settingsForm.ui_texts.intro_title || ''}
                          onBlur={(e) => setSettingsForm(prev => ({
                            ...prev,
                            ui_texts: { ...prev.ui_texts, intro_title: e.target.value }
                          }))}
                          className="h-9 bg-slate-600 border-slate-500 text-white"
                        />
                      </div>
                      <div className="grid md:grid-cols-[180px_minmax(0,1fr)] gap-2 items-start">
                        <label className="text-sm text-purple-400 font-medium md:pt-2">Message écrit</label>
                        <Textarea
                          defaultValue={settingsForm.ui_texts.intro_message || ''}
                          onBlur={(e) => setSettingsForm(prev => ({
                            ...prev,
                            ui_texts: { ...prev.ui_texts, intro_message: e.target.value }
                          }))}
                          rows={1}
                          onInput={autoResizeTextarea}
                          onFocus={autoResizeTextarea}
                          className="min-h-9 resize-none overflow-hidden bg-slate-600 border-slate-500 text-white"
                        />
                      </div>
                      <div className="grid md:grid-cols-[180px_minmax(0,1fr)] gap-2 items-start">
                        <label className="text-sm text-cyan-400 font-medium md:pt-2">Message vocal</label>
                        <Textarea
                          defaultValue={settingsForm.robot_texts.question_intro || ''}
                          onBlur={(e) => setSettingsForm(prev => ({
                            ...prev,
                            robot_texts: { ...prev.robot_texts, question_intro: e.target.value }
                          }))}
                          rows={1}
                          onInput={autoResizeTextarea}
                          onFocus={autoResizeTextarea}
                          className="min-h-9 resize-none overflow-hidden bg-slate-600 border-slate-500 text-white"
                        />
                      </div>
                      <div className="grid md:grid-cols-[180px_minmax(0,1fr)] gap-2 items-center">
                        <label className="text-sm text-purple-400 font-medium">Texte bouton introduction</label>
                        <Input
                          defaultValue={settingsForm.ui_texts.intro_button || ''}
                          onBlur={(e) => setSettingsForm(prev => ({
                            ...prev,
                            ui_texts: { ...prev.ui_texts, intro_button: e.target.value }
                          }))}
                          className="h-9 bg-slate-600 border-slate-500 text-white"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 flex justify-end pr-[30px] pb-3 overflow-visible">
                    <Button
                      onClick={handleSaveSettings}
                      className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 relative z-10 -translate-x-[6px] shadow-none hover:shadow-none hover:!bg-emerald-500 hover:!text-white"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer cette section
                    </Button>
                  </div>
                </CardContent>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Questions List with Reorder */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Questions actuelles (ordre de la timeline)</CardTitle>
                </CardHeader>
                <CardContent className="overflow-visible">
                  <div className="space-y-3">
                    {questions.map((q, idx) => {
                      const identityLockedAtTop = questions[0]?.type === 'identity';
                      const disableUp = idx === 0 || q.type === 'identity' || (identityLockedAtTop && idx === 1);
                      const disableDown = idx === questions.length - 1 || q.type === 'identity' || (identityLockedAtTop && idx === 0);
                      return (
                      <div 
                        key={q.id} 
                        className="p-4 bg-slate-700 rounded-lg flex items-start gap-4"
                      >
                        {/* Reorder buttons */}
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveQuestion(idx, 'up')}
                            disabled={disableUp}
                            className="h-6 w-6 p-0 text-slate-400 hover:text-white disabled:opacity-30"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <div className="text-slate-500 text-center text-xs font-bold">{idx + 1}</div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveQuestion(idx, 'down')}
                            disabled={disableDown}
                            className="h-6 w-6 p-0 text-slate-400 hover:text-white disabled:opacity-30"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="bg-slate-600 text-slate-200 text-xs px-2 py-1 rounded">
                              {questionTypeLabels[q.type] || q.type}
                            </span>
                          </div>
                          <p className="font-medium text-white">{q.text}</p>
                          <p className="text-sm text-slate-400 mt-1">Robot dit: "{q.robot_text}"</p>
                          {q.choices?.length > 0 && (
                            <p className="text-sm text-slate-500 mt-1">
                              Choix :{' '}
                              {q.choices.map((c, i) => (
                                <span key={i}>
                                  {choiceLabel(c)}
                                  {typeof c === 'object' && c?.voice_followup ? (
                                    <span className="text-cyan-400">
                                      {' '}
                                      (voix
                                      {c.followup_robot_text ? ` : « ${c.followup_robot_text} »` : ''})
                                    </span>
                                  ) : null}
                                  {i < q.choices.length - 1 ? ' | ' : ''}
                                </span>
                              ))}
                            </p>
                          )}
                          {q.thank_text ? (
                            <div className="text-xs text-cyan-400 mt-2">
                              <span>Remerciement robot : &quot;{q.thank_text}&quot;</span>
                            </div>
                          ) : null}
                        </div>
                        
                        <div className="flex gap-2">
                          <Dialog
                            open={isEditQuestionDialogOpen && editingQuestion?.id === q.id}
                            onOpenChange={(open) => {
                              setIsEditQuestionDialogOpen(open);
                              if (!open) setEditingQuestion(null);
                            }}
                          >
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const qIndex = questions.findIndex((x) => x.id === q.id);
                                  let t = q.type === 'multiple_choice_voice' ? 'multiple_choice' : q.type;
                                  // L’identité n’est valide qu’en première question ; corriger d’éventuelles données legacy.
                                  if (t === 'identity' && qIndex !== 0) {
                                    t = 'open_voice';
                                  }
                                  setEditingQuestion({
                                    ...q,
                                    type: t,
                                    choices: choicesForAdminForm(q.choices),
                                    thank_text: q.thank_text || ''
                                  });
                                  if (t === 'identity' && qIndex === 0) {
                                    const rt = settingsFormRef.current?.robot_texts || {};
                                    setIdentityRobotDraft(
                                      Object.fromEntries(
                                        IDENTITY_ROBOT_TEXT_KEYS.map((k) => [
                                          k,
                                          typeof rt[k] === 'string' ? rt[k] : '',
                                        ])
                                      )
                                    );
                                  } else {
                                    setIdentityRobotDraft(emptyIdentityRobotDraft());
                                  }
                                  setIsEditQuestionDialogOpen(true);
                                }}
                                className="border-slate-600 text-slate-200 hover:bg-slate-600 shadow-none hover:shadow-none"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-800 border-slate-700 max-h-[90vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle className="text-white">Modifier la question</DialogTitle>
                              </DialogHeader>
                              {editingQuestion && (
                                <div className="space-y-4">
                                  <div className="p-4 bg-slate-700/60 border border-cyan-400/40 rounded-lg space-y-2">
                                    <label className="text-sm font-semibold text-cyan-300">Type de question (déterminant)</label>
                                    {editingQuestion.type === 'identity' &&
                                    questions[0]?.id === editingQuestion.id ? (
                                      <p className="text-sm text-slate-200 rounded-md bg-slate-800/80 border border-slate-600 px-3 py-2">
                                        Identité (prénom) — toujours la première question.
                                      </p>
                                    ) : (
                                      <Select
                                        value={editingQuestion.type}
                                        onValueChange={(v) => setEditingQuestion((prev) => ({ ...prev, type: v }))}
                                      >
                                        <SelectTrigger className="bg-slate-700 border-slate-500 text-white">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-slate-700 border-slate-600">
                                          <SelectItem value="multiple_choice">Choix multiple</SelectItem>
                                          <SelectItem value="open_voice">Question ouverte</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    )}
                                  </div>

                                  <div>
                                    <label className="text-sm font-medium text-slate-300">Question (écran)</label>
                                    <Input
                                      value={editingQuestion.text}
                                      onChange={(e) => setEditingQuestion(prev => ({ ...prev, text: e.target.value }))}
                                      className="bg-slate-700 border-slate-600 text-white"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium text-slate-300">Texte robot (parlé)</label>
                                    <Textarea
                                      value={editingQuestion.robot_text}
                                      onChange={(e) => setEditingQuestion(prev => ({ ...prev, robot_text: e.target.value }))}
                                      className="bg-slate-700 border-slate-600 text-white"
                                    />
                                  </div>

                                  {editingQuestion.type === 'identity' && questions[0]?.id === editingQuestion.id ? (
                                    <>
                                      <div className="p-4 bg-slate-700/50 rounded-lg space-y-3">
                                        <p className="text-sm text-cyan-400 font-medium">Remerciement (optionnel)</p>
                                        <p className="text-xs text-slate-500">
                                          Après la validation du prénom écrit. Vous pouvez utiliser {'{prénom}'} ou{' '}
                                          {'{prenom}'} pour insérer le prénom saisi. Laisser vide : aucun message vocal.
                                        </p>
                                        <div>
                                          <label className="text-sm text-slate-300">Texte du robot</label>
                                          <Input
                                            value={editingQuestion.thank_text || ''}
                                            onChange={(e) =>
                                              setEditingQuestion((prev) => ({ ...prev, thank_text: e.target.value }))
                                            }
                                            placeholder=""
                                            className="bg-slate-700 border-slate-600 text-white"
                                          />
                                        </div>
                                      </div>

                                      {IDENTITY_ROBOT_TEXT_KEYS.map((key) => (
                                        <div key={key}>
                                          <label className="text-sm font-medium text-slate-300">
                                            {IDENTITY_VOICE_FIELD_LABELS[key] || key}
                                          </label>
                                          <Textarea
                                            value={identityRobotDraft[key] ?? ''}
                                            onChange={(e) =>
                                              setIdentityRobotDraft((prev) => ({
                                                ...prev,
                                                [key]: e.target.value,
                                              }))
                                            }
                                            rows={key === 'ask_name_audio' ? 2 : 1}
                                            onInput={autoResizeTextarea}
                                            onFocus={autoResizeTextarea}
                                            className="mt-1 bg-slate-700 border-slate-600 text-white"
                                          />
                                        </div>
                                      ))}
                                    </>
                                  ) : null}
                                  
                                  {editingQuestion.type !== 'open_voice' && editingQuestion.type !== 'identity' && (
                                    <div>
                                      <label className="text-sm font-medium text-slate-300">Choix</label>
                                      <p className="text-xs text-slate-500 mt-1 mb-2">
                                        Coche « En savoir plus (voix) » pour les réponses où l’enfant enregistre un message après son choix. Indique alors ce que dit le robot avant le micro (facultatif).
                                      </p>
                                      <div className="space-y-4 mt-2">
                                        {(editingQuestion.choices || []).map((choice, idx) => (
                                          <div
                                            key={idx}
                                            className="rounded-lg border border-slate-600/60 bg-slate-800/40 p-3 space-y-3"
                                          >
                                            <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:gap-3">
                                              <Input
                                                value={choiceLabel(choice)}
                                                onChange={(e) => {
                                                  const newChoices = [...(editingQuestion.choices || [])];
                                                  const cur =
                                                    newChoices[idx] && typeof newChoices[idx] === 'object'
                                                      ? { ...newChoices[idx] }
                                                      : {
                                                          label: choiceLabel(newChoices[idx]),
                                                          voice_followup: false,
                                                          followup_robot_text: '',
                                                        };
                                                  cur.label = e.target.value;
                                                  newChoices[idx] = cur;
                                                  setEditingQuestion((prev) => ({ ...prev, choices: newChoices }));
                                                }}
                                                placeholder={`Choix ${idx + 1}`}
                                                className="flex-1 bg-slate-700 border-slate-600 text-white"
                                              />
                                              <label className="flex items-center gap-2 text-sm text-slate-300 shrink-0 cursor-pointer select-none">
                                                <Checkbox
                                                  checked={Boolean(choice?.voice_followup)}
                                                  onCheckedChange={(checked) => {
                                                    const newChoices = [...(editingQuestion.choices || [])];
                                                    const cur =
                                                      newChoices[idx] && typeof newChoices[idx] === 'object'
                                                        ? { ...newChoices[idx] }
                                                        : {
                                                            label: choiceLabel(newChoices[idx]),
                                                            voice_followup: false,
                                                            followup_robot_text: '',
                                                          };
                                                    cur.voice_followup = checked === true;
                                                    if (!cur.voice_followup) cur.followup_robot_text = '';
                                                    newChoices[idx] = cur;
                                                    setEditingQuestion((prev) => ({ ...prev, choices: newChoices }));
                                                  }}
                                                />
                                                En savoir plus (voix)
                                              </label>
                                            </div>
                                            {Boolean(choice?.voice_followup) && (
                                              <div className="space-y-1 pl-0 sm:pl-1">
                                                <label className="text-xs font-medium text-cyan-400 block">
                                                  Texte du robot (avant l’enregistrement)
                                                </label>
                                                <Textarea
                                                  value={
                                                    typeof choice?.followup_robot_text === 'string'
                                                      ? choice.followup_robot_text
                                                      : ''
                                                  }
                                                  onChange={(e) => {
                                                    const newChoices = [...(editingQuestion.choices || [])];
                                                    const cur =
                                                      newChoices[idx] && typeof newChoices[idx] === 'object'
                                                        ? { ...newChoices[idx] }
                                                        : {
                                                            label: choiceLabel(newChoices[idx]),
                                                            voice_followup: true,
                                                            followup_robot_text: '',
                                                          };
                                                    cur.followup_robot_text = e.target.value;
                                                    newChoices[idx] = cur;
                                                    setEditingQuestion((prev) => ({ ...prev, choices: newChoices }));
                                                  }}
                                                  placeholder="Ex. : Tu veux m’en dire un peu plus ?"
                                                  rows={2}
                                                  className="bg-slate-700 border-slate-600 text-white text-sm min-h-[3rem] resize-y"
                                                />
                                                <p className="text-xs text-slate-500">
                                                  Laisser vide : le robot ne dit rien, l’enfant passe directement au micro.
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => setEditingQuestion(prev => ({
                                            ...prev,
                                            choices: [...(prev.choices || []), emptyChoiceForForm()]
                                          }))}
                                          className="text-cyan-400"
                                        >
                                          <Plus className="w-4 h-4 mr-1" /> Ajouter un choix
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {!(editingQuestion.type === 'identity' && questions[0]?.id === editingQuestion.id) ? (
                                  <div className="p-4 bg-slate-700/50 rounded-lg space-y-3">
                                    <p className="text-sm text-cyan-400 font-medium">Remerciement (optionnel)</p>
                                    <p className="text-xs text-slate-500">
                                      Après la réponse (ou l’enregistrement), phrase dite par le robot. Laisser vide : aucun message vocal.
                                    </p>
                                    <div>
                                      <label className="text-sm text-slate-300">Texte du robot</label>
                                      <Input
                                        value={editingQuestion.thank_text || ''}
                                        onChange={(e) => setEditingQuestion((prev) => ({ ...prev, thank_text: e.target.value }))}
                                        placeholder=""
                                        className="bg-slate-700 border-slate-600 text-white"
                                      />
                                    </div>
                                  </div>
                                  ) : null}
                                  
                                  <Button onClick={handleUpdateQuestion} className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 shadow-none hover:shadow-none hover:!bg-emerald-500 hover:!text-white">
                                    <Save className="w-4 h-4 mr-2" />
                                    Enregistrer
                                  </Button>
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteQuestion(q.id)}
                            className="bg-red-500/20 text-red-400 hover:bg-red-500/30 shadow-none hover:shadow-none"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  <div className="pt-4 flex justify-end pr-[30px] pb-3 overflow-visible">
                    <Dialog open={isAddQuestionDialogOpen} onOpenChange={setIsAddQuestionDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 relative z-10 shadow-none hover:shadow-none hover:!bg-emerald-500 hover:!text-white">
                          <Plus className="w-4 h-4 mr-2" />
                          Ajouter une question
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-slate-800 border-slate-700 max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-white flex items-center gap-2">
                            <Plus className="w-5 h-5" />
                            Ajouter une question
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                          <div className="p-4 bg-slate-700/60 border border-cyan-400/40 rounded-lg space-y-2">
                            <label className="text-sm font-semibold text-cyan-300">Type de question</label>
                            <p className="text-xs text-slate-400">
                              Choisis d’abord le format : choix proposés à l’écran ou réponse libre en voix.
                            </p>
                            <Select
                              value={newQuestion.type}
                              onValueChange={(v) => setNewQuestion((prev) => ({ ...prev, type: v }))}
                            >
                              <SelectTrigger className="bg-slate-700 border-slate-500 text-white h-11 text-base">
                                <SelectValue placeholder="Type de question" />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-700 border-slate-600">
                                <SelectItem value="multiple_choice">Choix multiple</SelectItem>
                                <SelectItem value="open_voice">Question ouverte</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-3">
                            <div>
                              <label className="text-sm font-medium text-slate-300 mb-1 block">Question (affichée à l'écran)</label>
                              <Input
                                value={newQuestion.text}
                                onChange={(e) => setNewQuestion((prev) => ({ ...prev, text: e.target.value }))}
                                placeholder="Comment tu te sens ?"
                                className="bg-slate-700 border-slate-600 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-slate-300 mb-1 block">Texte du robot (ce qu'il dit)</label>
                              <Input
                                value={newQuestion.robot_text}
                                onChange={(e) => setNewQuestion((prev) => ({ ...prev, robot_text: e.target.value }))}
                                placeholder="Comment tu te sens aujourd'hui ?"
                                className="bg-slate-700 border-slate-600 text-white"
                              />
                            </div>
                          </div>

                          {newQuestion.type !== 'open_voice' && newQuestion.type !== 'identity' && (
                            <div>
                              <label className="text-sm font-medium text-slate-300 mb-1 block">Choix de réponses</label>
                              <p className="text-xs text-slate-500 mb-2">
                                Coche « En savoir plus (voix) » pour proposer un enregistrement après ce choix, puis indiquez ce que dit le robot avant le micro (facultatif).
                              </p>
                              <div className="space-y-4">
                                {newQuestion.choices.map((choice, idx) => (
                                  <div
                                    key={idx}
                                    className="rounded-lg border border-slate-600/60 bg-slate-800/40 p-3 space-y-3"
                                  >
                                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:gap-3">
                                      <Input
                                        value={choiceLabel(choice)}
                                        onChange={(e) => {
                                          const newChoices = [...newQuestion.choices];
                                          const cur =
                                            newChoices[idx] && typeof newChoices[idx] === 'object'
                                              ? { ...newChoices[idx] }
                                              : {
                                                  label: choiceLabel(newChoices[idx]),
                                                  voice_followup: false,
                                                  followup_robot_text: '',
                                                };
                                          cur.label = e.target.value;
                                          newChoices[idx] = cur;
                                          setNewQuestion((prev) => ({ ...prev, choices: newChoices }));
                                        }}
                                        placeholder={`Choix ${idx + 1}`}
                                        className="flex-1 bg-slate-700 border-slate-600 text-white"
                                      />
                                      <label className="flex items-center gap-2 text-sm text-slate-300 shrink-0 cursor-pointer select-none">
                                        <Checkbox
                                          checked={Boolean(choice?.voice_followup)}
                                          onCheckedChange={(checked) => {
                                            const newChoices = [...newQuestion.choices];
                                            const cur =
                                              newChoices[idx] && typeof newChoices[idx] === 'object'
                                                ? { ...newChoices[idx] }
                                                : {
                                                    label: choiceLabel(newChoices[idx]),
                                                    voice_followup: false,
                                                    followup_robot_text: '',
                                                  };
                                            cur.voice_followup = checked === true;
                                            if (!cur.voice_followup) cur.followup_robot_text = '';
                                            newChoices[idx] = cur;
                                            setNewQuestion((prev) => ({ ...prev, choices: newChoices }));
                                          }}
                                        />
                                        En savoir plus (voix)
                                      </label>
                                    </div>
                                    {Boolean(choice?.voice_followup) && (
                                      <div className="space-y-1 pl-0 sm:pl-1">
                                        <label className="text-xs font-medium text-cyan-400 block">
                                          Texte du robot (avant l’enregistrement)
                                        </label>
                                        <Textarea
                                          value={
                                            typeof choice?.followup_robot_text === 'string'
                                              ? choice.followup_robot_text
                                              : ''
                                          }
                                          onChange={(e) => {
                                            const newChoices = [...newQuestion.choices];
                                            const cur =
                                              newChoices[idx] && typeof newChoices[idx] === 'object'
                                                ? { ...newChoices[idx] }
                                                : {
                                                    label: choiceLabel(newChoices[idx]),
                                                    voice_followup: true,
                                                    followup_robot_text: '',
                                                  };
                                            cur.followup_robot_text = e.target.value;
                                            newChoices[idx] = cur;
                                            setNewQuestion((prev) => ({ ...prev, choices: newChoices }));
                                          }}
                                          placeholder="Ex. : Tu veux m’en dire un peu plus ?"
                                          rows={2}
                                          className="bg-slate-700 border-slate-600 text-white text-sm min-h-[3rem] resize-y"
                                        />
                                        <p className="text-xs text-slate-500">
                                          Laisser vide : le robot ne dit rien avant le micro.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  type="button"
                                  onClick={() =>
                                    setNewQuestion((prev) => ({
                                      ...prev,
                                      choices: [...prev.choices, emptyChoiceForForm()],
                                    }))
                                  }
                                  className="text-cyan-400"
                                >
                                  <Plus className="w-4 h-4 mr-1" /> Ajouter un choix
                                </Button>
                              </div>
                            </div>
                          )}

                          <div className="p-3 bg-slate-700/50 rounded-lg space-y-2">
                            <label className="text-sm font-medium text-cyan-400 mb-1 block">
                              Remerciement robot (optionnel)
                            </label>
                            <p className="text-xs text-slate-500">
                              Après la réponse. Laisser vide : le robot ne dit rien.
                            </p>
                            <Input
                              value={newQuestion.thank_text}
                              onChange={(e) => setNewQuestion((prev) => ({ ...prev, thank_text: e.target.value }))}
                              placeholder=""
                              className="bg-slate-700 border-slate-600 text-white"
                            />
                          </div>

                          <Button
                            onClick={handleAddQuestion}
                            className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 shadow-none hover:shadow-none hover:!bg-emerald-500 hover:!text-white"
                            disabled={!newQuestion.text || !newQuestion.robot_text}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Ajouter
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>

              {/* Fin du parcours + interface */}
              <Accordion type="single" collapsible defaultValue="end-timeline" className="rounded-lg border border-slate-700 bg-slate-800 px-3 overflow-visible">
                <AccordionItem value="end-timeline" className="border-slate-700">
                  <AccordionTrigger className="admin-accordion-trigger text-white hover:no-underline">Fin du parcours & interface</AccordionTrigger>
                  <AccordionContent className="overflow-visible">
                <CardContent className="space-y-4 px-0 overflow-visible">
                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-3 block">
                      Textes du robot (fin uniquement)
                    </label>
                    <div className="space-y-2">
                      <div className="grid md:grid-cols-[180px_minmax(0,1fr)] gap-2 items-start">
                        <label className="text-sm text-cyan-400 font-medium md:pt-2" htmlFor="admin-robot-goodbye">
                          {robotTextLabels.goodbye || 'goodbye'}
                        </label>
                        <Textarea
                          id="admin-robot-goodbye"
                          value={settingsForm.robot_texts?.goodbye ?? ''}
                          onChange={(e) => {
                            setSettingsForm((prev) => ({
                              ...prev,
                              robot_texts: { ...(prev.robot_texts || {}), goodbye: e.target.value },
                            }));
                            autoResizeTextarea(e);
                          }}
                          rows={1}
                          onInput={autoResizeTextarea}
                          onFocus={autoResizeTextarea}
                          className="min-h-9 resize-none overflow-hidden bg-slate-600 border-slate-500 text-white"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-3 block">
                      Textes de l'interface (écran final)
                    </label>
                    <div className="space-y-2">
                      {['thank_title', 'thank_message', 'thank_proud', 'home_button'].map((key) => (
                        <div key={key} className="grid md:grid-cols-[180px_minmax(0,1fr)] gap-2 items-center">
                          <label className="text-sm text-purple-400 font-medium" htmlFor={`admin-ui-${key}`}>
                            {uiTextLabels[key] || key}
                          </label>
                          <Input
                            id={`admin-ui-${key}`}
                            value={settingsForm.ui_texts?.[key] ?? ''}
                            onChange={(e) =>
                              setSettingsForm((prev) => ({
                                ...prev,
                                ui_texts: { ...(prev.ui_texts || {}), [key]: e.target.value },
                              }))
                            }
                            className="h-9 bg-slate-600 border-slate-500 text-white"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="pt-4 flex justify-end pr-6 pb-3 overflow-visible">
                    <Button
                      onClick={handleSaveSettings}
                      className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 relative z-10 -translate-x-[6px] shadow-none hover:shadow-none hover:!bg-emerald-500 hover:!text-white"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer cette section
                    </Button>
                  </div>
                </CardContent>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </ThemeWrapper>
  );
};

export default AdminPanel;
